// Cart item component for displaying items in the cart

import {
  formatPrice,
  type CartItem as CartItemType,
} from "@/lib/queries/products";
import type { ConditionCriteria } from "@/lib/conditions";
import { Button } from "@repo/ui";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

interface CartItemProps {
  item: CartItemType;
  currencyCode?: string;
  showPrice?: boolean;
  /** When false, hides the Vendor badge. Driven by catalog `hideVendorsFromCreators`. */
  showVendor?: boolean;
  /** When false, hides Tag badges. Driven by catalog `hideTagsFromCreators`. */
  showTags?: boolean;
  onUpdateQuantity: (id: string, quantity: number) => void;
  onRemove: (id: string) => void;
  disableIncrement?: boolean;
  maxAllowedQuantity?: number;
  /** Collection names referenced by product conditions (lowercase). @deprecated Use conditionCriteria instead. */
  conditionCollectionNames?: Set<string>;
  /** All condition-relevant criteria for badge highlighting. */
  conditionCriteria?: ConditionCriteria;
}

export function CartItem({
  item,
  currencyCode = "USD",
  showPrice = true,
  showVendor = true,
  showTags = true,
  onUpdateQuantity,
  onRemove,
  disableIncrement = false,
  maxAllowedQuantity,
  conditionCollectionNames,
  conditionCriteria,
}: CartItemProps) {
  const handleDecrement = () => {
    if (item.quantity > 1) {
      onUpdateQuantity(item.id, item.quantity - 1);
    }
  };

  const handleIncrement = () => {
    onUpdateQuantity(item.id, item.quantity + 1);
  };

  return (
    <div className="flex gap-4 py-4 border-b border-border last:border-0 hover:bg-muted/5 transition-colors">
      {/* Product Image */}
      <div className="w-20 h-20 bg-muted rounded-lg overflow-hidden shrink-0 border border-border">
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
            No image
          </div>
        )}
      </div>

      {/* Product Details */}
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-foreground text-sm line-clamp-2">
          {item.title}
        </h4>
        {item.variantTitle && (
          <p className="text-xs text-muted-foreground mt-0.5">
            {item.variantTitle}
          </p>
        )}
        {showPrice && (
          <p className="text-sm font-medium text-foreground mt-1">
            {formatPrice(parseFloat(item.price), currencyCode)}
          </p>
        )}
        <CartItemBadges
          item={item}
          conditionCriteria={conditionCriteria}
          conditionCollectionNames={conditionCollectionNames}
          showVendor={showVendor}
          showTags={showTags}
        />
      </div>

      {/* Quantity Controls */}
      <div className="flex flex-col items-end justify-between">
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-destructive"
          onClick={() => onRemove(item.id)}
          aria-label="Remove item"
        >
          <Trash2 className="h-4 w-4" />
        </Button>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={handleDecrement}
            disabled={item.quantity <= 1}
            aria-label="Decrease quantity"
          >
            <Minus className="h-3 w-3" />
          </Button>
          <span className="w-6 text-center text-sm font-medium text-foreground">
            {item.quantity}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={handleIncrement}
            disabled={disableIncrement}
            aria-label="Increase quantity"
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>
        {maxAllowedQuantity != null && (
          <div className="text-xs text-muted-foreground mt-1">
            Max: {maxAllowedQuantity}
          </div>
        )}
      </div>
    </div>
  );
}

const VISIBLE_COUNT = 5;

const MATCH_CLASS =
  "inline-block text-[10px] leading-tight px-1.5 py-0.5 rounded-sm bg-teal-100 text-teal-800 border border-teal-200";
const MUTED_CLASS =
  "inline-block text-[10px] leading-tight px-1.5 py-0.5 rounded-sm bg-muted text-muted-foreground border border-border";

function CartItemBadges({
  item,
  conditionCriteria,
  conditionCollectionNames,
  showVendor = true,
  showTags = true,
}: {
  item: CartItemType;
  conditionCriteria?: ConditionCriteria;
  conditionCollectionNames?: Set<string>;
  showVendor?: boolean;
  showTags?: boolean;
}) {
  // Must be declared before any early returns to satisfy React's Rules of Hooks
  const [expanded, setExpanded] = useState(false);

  const badges: Array<{ label: string; matches: boolean }> = [];

  // Vendor — gated by catalog `hideVendorsFromCreators` (EPD-8604)
  if (showVendor && item.vendor) {
    badges.push({
      label: item.vendor,
      matches:
        conditionCriteria?.vendors.has(item.vendor.toLowerCase()) ?? false,
    });
  }

  // Product type
  if (item.productType) {
    badges.push({
      label: item.productType,
      matches:
        conditionCriteria?.productTypes.has(item.productType.toLowerCase()) ??
        false,
    });
  }

  // Category
  if (item.category) {
    badges.push({
      label: item.category,
      matches:
        conditionCriteria?.categories.has(item.category.toLowerCase()) ?? false,
    });
  }

  // Collections — use conditionCriteria if available, fall back to legacy prop
  if (item.collectionNames) {
    for (const name of item.collectionNames) {
      const matches = conditionCriteria
        ? conditionCriteria.collectionNames.has(name.toLowerCase())
        : (conditionCollectionNames?.has(name.toLowerCase()) ?? false);
      badges.push({ label: name, matches });
    }
  }

  // Tags — gated by catalog `hideTagsFromCreators` (EPD-8604)
  if (showTags && item.tags) {
    for (const tag of item.tags) {
      badges.push({
        label: tag,
        matches: conditionCriteria?.tags.has(tag.toLowerCase()) ?? false,
      });
    }
  }

  if (badges.length === 0) return null;

  const sorted = [...badges].sort(
    (a, b) => (b.matches ? 1 : 0) - (a.matches ? 1 : 0),
  );
  const visible = expanded ? sorted : sorted.slice(0, VISIBLE_COUNT);
  const hiddenCount = sorted.length - VISIBLE_COUNT;

  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {visible.map((badge, i) => (
        <span
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={`${badge.label}-${i}`}
          className={badge.matches ? MATCH_CLASS : MUTED_CLASS}
        >
          {badge.label}
        </span>
      ))}
      {!expanded && hiddenCount > 0 && (
        <button
          type="button"
          className="text-[10px] text-muted-foreground hover:text-foreground underline cursor-pointer bg-transparent border-0 p-0"
          onClick={() => setExpanded(true)}
        >
          +{hiddenCount} more
        </button>
      )}
      {expanded && sorted.length > VISIBLE_COUNT && (
        <button
          type="button"
          className="text-[10px] text-muted-foreground hover:text-foreground underline cursor-pointer bg-transparent border-0 p-0"
          onClick={() => setExpanded(false)}
        >
          show less
        </button>
      )}
    </div>
  );
}
